import { useEffect, useMemo, useState } from 'react';
import { glossaryGroups, glossaryTerms, glossaryRelated } from '@/mocks/glossary';
import { glossaryFaqItems } from '@/mocks/glossaryFaq';
import { getArticleBySlug } from '@/utils/content';
import TermCard from '@/pages/glossary/components/TermCard';
import GlossaryFaq from '@/pages/glossary/components/GlossaryFaq';
import ScrollReveal from '@/components/base/ScrollReveal';
import { siteOrigin, useSeo } from '@/hooks/useSeo';

const quickTerms = [
  { label: 'Pip', id: 'pip' },
  { label: 'Spread', id: 'spread' },
  { label: 'Leverage', id: 'leverage' },
  { label: 'Margin', id: 'margin' },
  { label: 'Lot', id: 'lot' },
  { label: 'Stop Loss', id: 'stop-loss' },
  { label: 'Take Profit', id: 'take-profit' },
  { label: 'Risk / Reward', id: 'risk-reward' },
  { label: 'Support', id: 'support' },
  { label: 'Resistance', id: 'resistance' },
];

export default function GlossaryPage() {
  const [activeGroup, setActiveGroup] = useState('all');
  const [focus, setFocus] = useState<{ id: string; n: number } | null>(null);

  const termMap = useMemo(() => new Map(glossaryTerms.map((term) => [term.id, term])), []);

  const relatedMap = useMemo(() => {
    const map = new Map<string, (typeof glossaryTerms)[number][]>();
    Object.entries(glossaryRelated).forEach(([id, ids]) => {
      map.set(
        id,
        ids.map((rid) => termMap.get(rid)).filter((item): item is (typeof glossaryTerms)[number] => Boolean(item)),
      );
    });
    return map;
  }, [termMap]);

  const handleSelectTerm = (id: string) => {
    setActiveGroup('all');
    setFocus((prev) => ({ id, n: (prev?.n ?? 0) + 1 }));
  };

  const handleSelectGroup = (groupId: string) => {
    setActiveGroup(groupId);
    const el = document.getElementById(groupId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (!focus) return;
    const el = document.getElementById(`term-${focus.id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(() => setFocus(null), 2200);
    return () => window.clearTimeout(timer);
  }, [focus]);

  const filtered = useMemo(
    () => glossaryTerms.filter((term) => activeGroup === 'all' || term.group === activeGroup),
    [activeGroup],
  );

  const jsonLd = useMemo(() => {
    const origin = siteOrigin();
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'DefinedTermSet',
        name: 'คำศัพท์ Forex',
        description: 'รวมคำศัพท์ Forex ที่มือใหม่ควรรู้ พร้อมความหมายและบทความอธิบายเพิ่มเติม',
        inLanguage: 'th-TH',
        hasDefinedTerm: glossaryTerms.map((term) => ({
          '@type': 'DefinedTerm',
          name: term.term,
          description: term.definition,
          inDefinedTermSet: `${origin}/glossary`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: glossaryFaqItems.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `${item.a} ${item.a2}`,
          },
        })),
      },
    ];
  }, []);

  useSeo({
    /* @replace:glossary-title */
    title: 'คำศัพท์ Forex | รวมศัพท์เทรดที่มือใหม่ควรรู้',
    description:
      /* @replace:glossary-description */
      'รวมคำศัพท์ Forex ภาษาไทยที่มือใหม่ควรรู้ ทั้ง Pip Spread Leverage Margin Stop Loss พร้อมคำอธิบายง่าย ๆ และลิงก์ไปบทความที่เกี่ยวข้อง',
    /* @replace:glossary-keywords */
    keywords: 'คำศัพท์ Forex, Pip, Spread, Leverage, Margin, Stop Loss, Bid Ask, ศัพท์เทรด',
    canonicalPath: '/glossary',
    jsonLd,
  });

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16 lg:px-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px] lg:items-start lg:gap-12">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-translate-2" />
                คลังคำศัพท์สำหรับมือใหม่
              </span>
              <h1 className="mt-5 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-5xl">
                คำศัพท์ Forex
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                รวมคำศัพท์ Forex ที่มือใหม่ควรรู้ไว้ในที่เดียว ตั้งแต่ Pip Spread Leverage ไปจนถึง Stop Loss
                และการบริหารความเสี่ยง อธิบายด้วยภาษาง่าย ๆ พร้อมลิงก์ไปบทความที่เกี่ยวข้องในแต่ละหมวด
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-book-open-line text-base text-primary-600" />
                  อธิบายด้วยภาษาไทยเข้าใจง่าย
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-link text-base text-primary-600" />
                  ทุกคำศัพท์มีบทความต่อยอด
                </span>
              </div>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-50/70 p-4 md:mt-9 md:p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-foreground-500">
                    <i className="ri-route-line text-base text-primary-600" />
                    ลำดับการเรียนรู้
                  </span>
                  <span className="hidden text-xs text-foreground-500 sm:inline">ลำดับที่แนะนำสำหรับมือใหม่</span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-2">
                  {glossaryGroups.map((group, index) => (
                    <div key={group.id} className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSelectGroup(group.id)}
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                          activeGroup === group.id
                            ? 'border-primary-300 bg-primary-100 text-primary-800'
                            : 'border-background-300/70 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800'
                        }`}
                      >
                        <span className="font-num text-[11px] font-semibold text-primary-600">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <i className={`${group.icon} text-sm text-primary-600`} />
                        {group.label}
                      </button>
                      {index < glossaryGroups.length - 1 && (
                        <i className="ri-arrow-right-line hidden text-sm text-background-400 sm:inline" />
                      )}
                    </div>
                  ))}
                </div>

                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  เริ่มจากคำศัพท์พื้นฐานก่อน แล้วค่อยขยับไปคำสั่งเปิดออเดอร์ การอ่านกราฟ และการบริหารความเสี่ยง
                </p>
              </div>
            </div>

            <div className="w-full rounded-lg border border-background-300/60 bg-background-50 p-5 md:p-6 lg:w-[360px]">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-foreground-500">
                <i className="ri-bar-chart-box-line text-base text-primary-600" />
                ภาพรวมคลังคำศัพท์
              </div>

              <div className="mt-4 grid grid-cols-2 divide-x divide-background-200">
                <div className="pr-4">
                  <p className="font-num text-3xl font-semibold text-primary-700">{glossaryTerms.length}</p>
                  <p className="mt-1 text-xs text-foreground-500">คำศัพท์ทั้งหมด</p>
                </div>
                <div className="pl-4">
                  <p className="font-num text-3xl font-semibold text-accent-600">{glossaryGroups.length}</p>
                  <p className="mt-1 text-xs text-foreground-500">หมวดคำศัพท์</p>
                </div>
              </div>

              <div className="mt-5 border-t border-background-200 pt-5">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-foreground-500">
                  <i className="ri-lightbulb-line text-base text-accent-500" />
                  คำศัพท์ที่ควรรู้ก่อน
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {quickTerms.map((term) => (
                    <button
                      key={term.id}
                      type="button"
                      onClick={() => handleSelectTerm(term.id)}
                      className="rounded-full border border-background-300/70 bg-background-100/70 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:bg-accent-50 hover:text-accent-800"
                    >
                      {term.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-50/95 backdrop-blur lg:sticky lg:top-[72px] lg:z-30">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-4 py-4 md:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <div className="flex items-center justify-between gap-3 lg:justify-start lg:gap-4">
            <span className="hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-foreground-500 lg:flex">
              <i className="ri-filter-3-line text-base" />
              หมวดคำศัพท์
            </span>
            <span className="whitespace-nowrap text-sm text-foreground-500">
              พบ <span className="font-num font-semibold text-foreground-950">{filtered.length}</span> คำศัพท์
            </span>
          </div>

          <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-0.5 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0">
            <button
              type="button"
              onClick={() => setActiveGroup('all')}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                activeGroup === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
              }`}
            >
              ทั้งหมด
            </button>
            {glossaryGroups.map((group) => (
              <button
                key={group.id}
                type="button"
                onClick={() => setActiveGroup(group.id)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  activeGroup === group.id
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {group.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14 lg:px-10">
        <div className="flex flex-col gap-14">
          {glossaryGroups.map((group) => {
            const groupTerms = filtered.filter((term) => term.group === group.id);
            if (groupTerms.length === 0) return null;
            return (
              <div key={group.id} id={group.id} className="scroll-mt-32">
                <ScrollReveal>
                  <div className="flex items-center gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-xl text-primary-600">
                      <i className={group.icon} />
                    </span>
                    <div>
                      <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{group.label}</h2>
                      <p className="mt-0.5 text-sm text-foreground-600">{group.desc}</p>
                    </div>
                    <span className="ml-auto hidden font-num text-sm text-foreground-500 sm:block">
                      {groupTerms.length} คำ
                    </span>
                  </div>
                </ScrollReveal>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {groupTerms.map((term, index) => {
                    const related = relatedMap.get(term.id) ?? [];
                    return (
                      <ScrollReveal key={term.id} delay={(index % 3) * 80}>
                        <TermCard
                          term={term}
                          article={getArticleBySlug(term.articleSlug)}
                          onSelectTerm={handleSelectTerm}
                          related={related}
                          highlighted={focus?.id === term.id}
                        />
                      </ScrollReveal>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <GlossaryFaq onSelectTerm={handleSelectTerm} />
    </div>
  );
}